import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import { QueryState } from "@/components/common/AsyncState";
import { KpiCard } from "@/components/common/KpiCard";
import { fetchBranches } from "@/api/branches";
import { fetchSalarySummaries, paySalary } from "@/api/finance";
import { currentPeriod, formatMoney, formatPercent, lastNPeriods, periodLabel } from "@/utils/format";
import { extractErrorMessage } from "@/types/common";
import type { TeacherSalarySummary } from "@/types/finance";

function PaySalaryDialog({ summary, onClose }: { summary: TeacherSalarySummary | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(summary?.toPay ?? 0);
  const [comment, setComment] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => paySalary(summary!.teacherId, { period: summary!.period, amount, comment: comment || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["salary-summaries"] });
      onClose();
    },
    onError: (error) => setServerError(extractErrorMessage(error)),
  });

  return (
    <Dialog open={Boolean(summary)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Выплата зарплаты — {summary?.teacherName}</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}
        <Stack spacing={2} mt={1}>
          <Alert severity="info">К выплате за {summary && periodLabel(summary.period)}: {formatMoney(summary?.toPay ?? 0)}</Alert>
          <TextField label="Сумма выплаты" type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} />
          <TextField label="Комментарий" value={comment} onChange={(e) => setComment(e.target.value)} multiline minRows={2} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" disabled={!amount || mutation.isPending} onClick={() => mutation.mutate()}>
          Выплатить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function SalaryTab() {
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const [branchId, setBranchId] = useState("");
  const [period, setPeriod] = useState(currentPeriod());
  const [payingSummary, setPayingSummary] = useState<TeacherSalarySummary | null>(null);

  const periods = lastNPeriods(6);

  const { data: summaries, isLoading, error } = useQuery({
    queryKey: ["salary-summaries", period, branchId],
    queryFn: () => fetchSalarySummaries(period, branchId || undefined),
  });

  const rows = summaries ?? [];
  const totalRevenue = rows.reduce((sum, s) => sum + s.revenue, 0);
  const totalAccrued = rows.reduce((sum, s) => sum + s.accrued, 0);
  const totalPaid = rows.reduce((sum, s) => sum + s.paidOut, 0);
  const totalLeft = rows.reduce((sum, s) => sum + s.toPay, 0);

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Приход групп" value={formatMoney(totalRevenue)} hint={periodLabel(period)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Фонд преподавателей" value={formatMoney(totalAccrued)} hint="по индивидуальному %" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Выплачено" value={formatMoney(totalPaid)} hint="включая авансы" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Осталось выплатить" value={formatMoney(totalLeft)} hint="после закрытия месяца" />
        </Grid>
      </Grid>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={2}>
        <TextField select label="Период" size="small" value={period} onChange={(e) => setPeriod(e.target.value)} sx={{ minWidth: 180 }}>
          {periods.map((p) => (
            <MenuItem key={p} value={p}>
              {periodLabel(p)}
            </MenuItem>
          ))}
        </TextField>
        <TextField select label="Филиал" size="small" value={branchId} onChange={(e) => setBranchId(e.target.value)} sx={{ minWidth: 200 }}>
          <MenuItem value="">Все филиалы</MenuItem>
          {(branches ?? []).map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Преподаватель</TableCell>
              <TableCell>Приход групп</TableCell>
              <TableCell>Процент</TableCell>
              <TableCell>Начислено</TableCell>
              <TableCell>Выплачено</TableCell>
              <TableCell>Остаток к выплате</TableCell>
              <TableCell align="right">Действие</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(summaries ?? []).map((s) => (
              <TableRow key={s.teacherId}>
                <TableCell>{s.teacherName}</TableCell>
                <TableCell>{formatMoney(s.revenue)}</TableCell>
                <TableCell>{formatPercent(s.percent)}</TableCell>
                <TableCell>{formatMoney(s.accrued)}</TableCell>
                <TableCell>{formatMoney(s.paidOut)}</TableCell>
                <TableCell>{formatMoney(s.toPay)}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="contained" disabled={s.toPay <= 0} onClick={() => setPayingSummary(s)}>
                    Выплатить
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {(summaries ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Нет данных за выбранный период.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </QueryState>
      <PaySalaryDialog summary={payingSummary} onClose={() => setPayingSummary(null)} />
    </>
  );
}
